import {
  IPublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser"

import { log, SHOW_LOGS } from "./logging"

export type AcquireTokenErrorAction = "logout" | "refresh"

export interface AcquireTokenCallbacks {
  setToken: (token: string | undefined) => void
  setAdb2cToken: (token: string) => void
  setErrorLoadingToken: (error: boolean) => void
  handleError: (params: {
    action?: AcquireTokenErrorAction
    log: { message: string; error: unknown }
  }) => void
}

export interface AcquireTokenResult {
  success: boolean
  token?: string
  error?: unknown
}


const getPolicyFromAccount = (
  account: { idTokenClaims?: Record<string, unknown> }
): string | undefined => {
  // tfp = Trust Framework Policy, acr = Authentication Context Class Reference
  return (account.idTokenClaims?.tfp || account.idTokenClaims?.acr) as string | undefined
}

const getTenantNameFromEnvironment = (environment: string): string => {
  const tenant = environment.replace(".b2clogin.com", "")
  return `${tenant}.onmicrosoft.com`
}

export const acquireToken = async (
  instance: IPublicClientApplication,
  callbacks: AcquireTokenCallbacks,
): Promise<AcquireTokenResult> => {
  const { setToken, setAdb2cToken, setErrorLoadingToken, handleError } =
    callbacks

  const activeAccount = instance.getActiveAccount()
  const accounts = instance.getAllAccounts()

  if (!activeAccount && accounts.length === 0) {
    log(SHOW_LOGS.ADB2C_TOKEN, "No accounts found")
    return { success: false }
  }

  const account = activeAccount || accounts[0]

  const policy = getPolicyFromAccount(account)
  
  const tenantName = getTenantNameFromEnvironment(account.environment)
  const authority = policy
    ? `https://${account.environment}/${tenantName}/${policy}`
    : undefined

  log(SHOW_LOGS.ADB2C_TOKEN, `Acquiring token silently with policy: ${policy}`)

  try {
    const response = await instance.acquireTokenSilent({
      scopes: ["openid", "offline_access"],
      account,
      authority,
      forceRefresh: false,
    })

    if (response.idToken) {
      log(SHOW_LOGS.ADB2C_TOKEN, "Got Azure B2C token")
      setToken(undefined)
      setAdb2cToken(response.idToken)
      return { success: true, token: response.idToken }
    }

    return { success: false }
  } catch (error) {
    log(SHOW_LOGS.ADB2C_TOKEN, "Failed to acquire token:", error)

    const errorMessage =
      error instanceof Error ? error.message : String(error)
    const isPolicyMismatch =
      errorMessage.includes("400") ||
      errorMessage.includes("invalid_grant") ||
      errorMessage.includes("AADB2C")

    if (isPolicyMismatch) {
      log(
        SHOW_LOGS.ADB2C_TOKEN,
        "Policy mismatch or stale token detected, clearing cache and redirecting",
      )
      await instance.clearCache()
    }

    if (error instanceof InteractionRequiredAuthError || isPolicyMismatch) {
      log(
        SHOW_LOGS.ADB2C_TOKEN,
        "Interaction required, redirecting to login",
      )

      const redirectPolicy = getPolicyFromAccount(account)
      const redirectTenantName = getTenantNameFromEnvironment(account.environment)
      const redirectAuthority = redirectPolicy
        ? `https://${account.environment}/${redirectTenantName}/${redirectPolicy}`
        : undefined

      instance.loginRedirect({
        scopes: ["openid", "offline_access"],
        authority: redirectAuthority,
      })
      return { success: false, error }
    }

    setErrorLoadingToken(true)
    handleError({
      action: "logout",
      log: { message: "Failed to acquire B2C token", error },
    })

    return { success: false, error }
  }
}

